import { SplitTypeWrapper } from "../../components/SplitTypeWrapper";
import { getGsap, getScrollTrigger } from "../global/GsapGlobals";
import { DUR, EASE, STAGGER, motionOK } from "../global/MotionTokens";

const SECTION_SELECTOR = "[data-theh-metrics-section]";
const VALUE_SELECTOR = "[data-theh-metric-value]";
const DIGIT_PATTERN = /^\d$/;

type DigitReel = {
    digit: HTMLElement;
    track: HTMLElement;
    steps: number;
    digitIndex: number;
};

function buildDigitReel(
    char: HTMLElement,
    targetDigit: number,
    metricIndex: number,
    digitIndex: number,
): DigitReel {
    const cycles = 2 + digitIndex + (metricIndex % 2);
    const steps = cycles * 10 + targetDigit;
    const track = document.createElement("span");
    track.className = "theh-metric-reel__track";
    track.setAttribute("aria-hidden", "true");

    for (let step = 0; step <= steps; step += 1) {
        const digit = document.createElement("span");
        digit.className = "theh-metric-reel__digit";
        digit.textContent = String(step % 10);
        track.appendChild(digit);
    }

    char.textContent = "";
    char.classList.add("theh-metric-reel");
    char.setAttribute("aria-hidden", "true");
    char.appendChild(track);

    const firstDigit = track.firstElementChild;

    return {
        digit: firstDigit instanceof HTMLElement ? firstDigit : char,
        track,
        steps,
        digitIndex,
    };
}

export class ThehMetricRollController {
    static init(): void {
        const sections = Array.from(document.querySelectorAll<HTMLElement>(SECTION_SELECTOR));
        if (!sections.length) return;

        sections.forEach((section) => {
            if (section.dataset.thehMetricsBound === "1") return;
            section.dataset.thehMetricsBound = "1";

            const values = Array.from(section.querySelectorAll<HTMLElement>(VALUE_SELECTOR));
            if (!values.length || !motionOK()) return;

            const registerTrigger = (): void => {
                if (section.dataset.thehMetricsTriggerBound === "1") return;
                section.dataset.thehMetricsTriggerBound = "1";

                const ScrollTrigger = getScrollTrigger();
                ScrollTrigger.create({
                    trigger: section,
                    start: "top 72%",
                    once: true,
                    invalidateOnRefresh: true,
                    onEnter: () => {
                        if (section.dataset.thehMetricsPlayed === "1") return;
                        section.dataset.thehMetricsPlayed = "1";

                        const gsap = getGsap();
                        const originalValues = new Map(
                            values.map((element) => [element, element.textContent ?? ""]),
                        );
                        const timeline = gsap.timeline({
                            paused: true,
                            onComplete: () => {
                                originalValues.forEach((value, element) => {
                                    element.textContent = value;
                                    element.dataset.thehMetricSettled = "1";
                                });
                            },
                        });

                        values.forEach((element, metricIndex) => {
                            const { chars } = SplitTypeWrapper.splitChars(element);
                            if (!chars.length) return;

                            const reels: DigitReel[] = [];
                            const suffixes: HTMLElement[] = [];
                            let digitIndex = 0;

                            chars.forEach((char) => {
                                const glyph = char.textContent ?? "";
                                if (DIGIT_PATTERN.test(glyph)) {
                                    reels.push(buildDigitReel(char, Number(glyph), metricIndex, digitIndex));
                                    digitIndex += 1;
                                    return;
                                }

                                char.classList.add("theh-metric-reel__suffix");
                                char.setAttribute("aria-hidden", "true");
                                suffixes.push(char);
                            });

                            const metricStart = metricIndex * STAGGER.slow;

                            reels.forEach(({ digit, track, steps, digitIndex: reelIndex }) => {
                                timeline.fromTo(
                                    track,
                                    { y: 0, willChange: "transform" },
                                    {
                                        y: () => -steps * digit.getBoundingClientRect().height,
                                        duration: DUR.epic + (reelIndex + 1) * DUR.fast,
                                        ease: EASE.outExpo,
                                        force3D: true,
                                        onComplete: () => gsap.set(track, { willChange: "auto" }),
                                    },
                                    metricStart,
                                );
                            });

                            if (suffixes.length) {
                                timeline.fromTo(
                                    suffixes,
                                    { yPercent: 70, opacity: 0 },
                                    {
                                        yPercent: 0,
                                        opacity: 1,
                                        duration: DUR.slow,
                                        ease: EASE.outExpo,
                                        stagger: STAGGER.fast,
                                    },
                                    metricStart + DUR.epic,
                                );
                            }
                        });

                        timeline.play(0);
                    },
                });
            };

            if (document.readyState === "complete") {
                registerTrigger();
            } else {
                window.addEventListener("load", registerTrigger, { once: true });
            }
        });
    }
}
